import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import type { PanInfo } from 'framer-motion';
import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { twJoin, twMerge } from 'tailwind-merge';

import { Button } from '../../../DesignSystem/Inputs/Button';

const messages = defineMessages({
  previousLabel: {
    defaultMessage: `Previous`,
    id: 'JJNc3c',
  },
  nextLabel: {
    defaultMessage: `Next`,
    id: '9+Ddtu',
  },
});

export type ControlClickArgs = {
  index: number;
  direction: 'left' | 'right';
};

export type CarouselProps = {
  children: ReactNode[];
  slidesToScroll?: number;
  onControlClick?: (args: ControlClickArgs) => void;
  lastCard?: ReactNode;
  isActive?: boolean;
};

export function Carousel(props: CarouselProps) {
  const {
    children,
    slidesToScroll = 3,
    onControlClick,
    lastCard,
    isActive,
  } = props;
  const { formatMessage } = useIntl();

  const childrenLength = lastCard ? children.length + 1 : children.length;

  let [index, setIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const itemRef = useRef<HTMLDivElement | null>(null);
  const slideWidth = itemRef.current?.clientWidth ?? 0;
  const gapWidth = 12;

  const containerWidth = containerRef.current?.clientWidth ?? 0;
  const leftmostPosition = Math.min(
    0,
    containerWidth - childrenLength * (slideWidth + gapWidth)
  );

  const xPosition = Math.max(
    leftmostPosition,
    -(index * (slideWidth + gapWidth))
  );

  const handlePrevious = () => {
    setIndex((prevIndex) => {
      const nextIndex = Math.max(0, prevIndex - slidesToScroll);
      onControlClick?.({ index: nextIndex, direction: 'left' });
      return nextIndex;
    });
  };

  const handleNext = () => {
    const maxIndex = childrenLength - 1;
    setIndex((prevIndex) => {
      const nextIndex = Math.min(maxIndex, prevIndex + slidesToScroll);
      onControlClick?.({
        direction: 'right',
        index: nextIndex + slidesToScroll,
      });
      return nextIndex;
    });
  };

  const handleEndDrag = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    dragProps: PanInfo
  ) => {
    const dragThreshold = 10;
    const clientWidth = containerRef.current?.clientWidth ?? 0;

    const { offset } = dragProps;

    if (offset.x > clientWidth / dragThreshold && index > 0) {
      handlePrevious();
    } else if (
      offset.x < -clientWidth / dragThreshold &&
      index < childrenLength - 1
    ) {
      handleNext();
    }
  };

  return (
    <div className="bg-white -ml-1 h-full">
      <div className="border-white mx-auto flex h-full max-w-7xl flex-col justify-center">
        <div className="relative m-auto w-[960px] max-w-[100%]">
          <div className="overflow-hidden">
            <motion.div
              initial={false}
              ref={containerRef}
              whileTap={{
                cursor:
                  childrenLength > slidesToScroll ? 'grabbing' : 'default',
              }}
              animate={{ x: xPosition }}
              transition={{ duration: 0.9, ease: [0.32, 0.72, 0, 1] }}
            >
              <motion.div
                className="flex gap-[12px]"
                drag={childrenLength > slidesToScroll ? 'x' : undefined}
                dragElastic={0.3}
                onDragEnd={handleEndDrag}
                dragConstraints={{ right: 0, left: 0 }}
              >
                {children.map((item: React.ReactNode, index: number) => (
                  <>
                    <div
                      key={index}
                      ref={index === 0 ? itemRef : null}
                      className={twJoin(index === 0 && 'ml-1')}
                    >
                      {item}
                    </div>
                  </>
                ))}
                <div>{lastCard}</div>
              </motion.div>
            </motion.div>
          </div>

          {index > 0 && (
            <div className="absolute left-0 top-0 flex h-full w-10 items-center justify-center bg-white-gradient-2">
              <Button
                onClick={handlePrevious}
                variation="secondaryLite"
                className="absolute -left-2 h-8 w-8 !p-0 transition"
                aria-label={formatMessage(messages.previousLabel)}
              >
                <ArrowLeftIcon className="h-4 w-4" />
              </Button>
            </div>
          )}

          {index < childrenLength - slidesToScroll && (
            <div
              className={twMerge(
                'absolute right-0 top-0 flex h-full w-10 items-center justify-center',
                isActive ? 'bg-white-gradient-3' : 'bg-white-gradient-1'
              )}
            >
              <Button
                onClick={handleNext}
                variation="secondaryLite"
                className="absolute -right-3 h-8 w-8 !p-0 transition"
                aria-label={formatMessage(messages.nextLabel)}
              >
                <ArrowRightIcon className="h-4 w-4 text-gray-8" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
